import React, { useState, useEffect } from "react";
import Sidebar from "../mini-comp/Sidebar";
import Topbar from "../mini-comp/Topbar";
import axios from "axios";
import Cookies from "js-cookie";
import API_BASE_URL from "../../../config/api";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCalendarLine,
  RiPhoneLine,
  RiMailLine,
  RiMapPinLine,
  RiCheckboxCircleLine,
} from "react-icons/ri";

const CompanyDetails = () => {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    package_id: '',
    end_date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!companyId) {
      setError("Company ID is missing");
      setLoading(false);
      return;
    }

    const token = Cookies.get("token");
    if (!token) {
      setError("No token found in cookies");
      setLoading(false);
      return;
    }

    axios
      .get(`${API_BASE_URL}/api/v1/billing/companies/${companyId}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("Company details response:", response.data);
        // Handle different response structures
        const companyData = response.data.company || response.data;
        setCompany(companyData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching company details:", error);
        setError(
          error.response?.data?.message || "Failed to load company details"
        );
        setLoading(false);
      });
  }, [companyId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const fetchPackages = () => {
    const token = Cookies.get("token");
    if (!token) {
      return;
    }

    setLoadingPackages(true);
    axios
      .get(`${API_BASE_URL}/api/v1/billing/packages/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("Packages response:", response.data);
        const packagesData = response.data.packages || response.data || [];
        setPackages(Array.isArray(packagesData) ? packagesData : []);
        setLoadingPackages(false);
      })
      .catch((error) => {
        console.error("Error fetching packages:", error);
        setLoadingPackages(false);
      });
  };

  const handleSubscriptionInputChange = (e) => {
    const { name, value } = e.target;
    setSubscriptionFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenSubscriptionModal = () => {
    fetchPackages();
    setShowSubscriptionModal(true);
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setSubscriptionFormData({
      package_id: '',
      end_date: ''
    });
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const token = Cookies.get("token");
    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No token found in cookies",
      });
      setSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/billing/companies/${companyId}/subscription/`,
        {
          package_id: subscriptionFormData.package_id,
          end_date: subscriptionFormData.end_date
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Subscription created successfully:", response.data);

      // Close modal
      handleCloseSubscriptionModal();

      // Refresh company details
      const companyResponse = await axios.get(
        `${API_BASE_URL}/api/v1/billing/companies/${companyId}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const companyData = companyResponse.data.company || companyResponse.data;
      setCompany(companyData);

      setSubmitting(false);
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Subscription added successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error creating subscription:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to add subscription. Please try again.",
      });
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Topbar />
        <Sidebar />
        <div className="app-content-area">
          <div className="app-content-wrap">
            <div className="container-fluid">
              <div className="row">
                <div className="col-xxl-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <span className="text-muted">
                        Loading company details...
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Topbar />
        <Sidebar />
        <div className="app-content-area">
          <div className="app-content-wrap">
            <div className="container-fluid">
              <div className="row">
                <div className="col-xxl-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <span className="text-danger">Error: {error}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div>
        <Topbar />
        <Sidebar />
        <div className="app-content-area">
          <div className="app-content-wrap">
            <div className="container-fluid">
              <div className="row">
                <div className="col-xxl-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <span className="text-muted">Company not found</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Topbar />
      <Sidebar />

      <>
        {/* app-content-area-start */}
        <div className="app-content-area">
          <div className="app-content-wrap">
            <div className="container-fluid">
              <div className="row">
                <div className="col-xxl-12 col-xl-12 col-lg-12">
                  <div className="sidebar-sticky">
                    <div className="card">
                      <div className="company-info">
                        <div className="company-logo">
                          {company.logo ? (
                            <img
                              src={API_BASE_URL + company.logo}
                              alt={company.company_name || "Company Logo"}
                            />
                          ) : (
                            <img
                              src="assets/images/company/company-thumb-001.png"
                              alt="Company Logo"
                            />
                          )}
                        </div>
                        <h2 className="company-name mb-15">
                          {company.trade_name ||
                            company.company_name ||
                            "Unnamed Company"}
                        </h2>

                        <div className="company-info-list mb-25">
                          <div className="row">
                            <div className="col-xl-6 col-lg-6 col-md-6 mb-3">
                              <h5 className="mb-15">Contact Information</h5>
                              <ul>
                                {company.address && (
                                  <li>
                                    <span>
                                      <RiMapPinLine />
                                    </span>
                                    {company.address}
                                    {company.city ? `, ${company.city}` : ""}
                                  </li>
                                )}
                                {company.phone_number && (
                                  <li>
                                    <span>
                                      <RiPhoneLine />
                                    </span>
                                    <a href={`tel:${company.phone_number}`}>
                                      {company.phone_number}
                                    </a>
                                  </li>
                                )}
                                {company.email && (
                                  <li>
                                    <span>
                                      <RiMailLine />
                                    </span>
                                    <a href={`mailto:${company.email}`}>
                                      {company.email}
                                    </a>
                                  </li>
                                )}
                                {company.has_subscription !== undefined && (
                                  <li>
                                    <span>
                                      <RiCheckboxCircleLine />
                                    </span>
                                    <span
                                      className={`badge ${company.has_subscription
                                          ? "bg-label-success"
                                          : "bg-label-danger"
                                        }`}
                                    >
                                      {company.has_subscription
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  </li>
                                )}
                              </ul>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-md-6 mb-3">
                              <h5 className="mb-15">Company Details</h5>
                              <ul>
                                {company.city && (
                                  <li>
                                    <span className="fw-6">City:</span>{" "}
                                    {company.city}
                                  </li>
                                )}
                                {company.employee_id_prefix && (
                                  <li>
                                    <span className="fw-6">
                                      Employee ID Prefix:
                                    </span>{" "}
                                    {company.employee_id_prefix}
                                  </li>
                                )}
                                {company.gratuity_increment_type && (
                                  <li>
                                    <span className="fw-6">
                                      Gratuity Increment Type:
                                    </span>{" "}
                                    {company.gratuity_increment_type === "fixed"
                                      ? "Fixed Rate"
                                      : company.gratuity_increment_type ===
                                        "incremental"
                                        ? "Incremental Rate"
                                        : company.gratuity_increment_type}
                                  </li>
                                )}
                                {company.gratuity_increment_type ===
                                  "incremental" &&
                                  company.gratuity_start_years && (
                                    <li>
                                      <span className="fw-6">
                                        Gratuity Start Years:
                                      </span>{" "}
                                      {company.gratuity_start_years} Years
                                    </li>
                                  )}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="company-info-list mb-25">
                          <div className="row">
                            <div className="col-xl-6 col-lg-6 col-md-6 mb-3">
                              <h5 className="mb-15">Subscription Timeline</h5>
                              <ul>
                                {company.subscription ? (
                                  <>
                                    <li>
                                      <span></span> Subscription Date:{" "}
                                      {formatDate(
                                        company.subscription.start_date
                                      )}
                                    </li>
                                    <li>
                                      <span></span> Expiry Date:{" "}
                                      {formatDate(
                                        company.subscription.end_date
                                      )}
                                    </li>
                                  </>
                                ) : (
                                  <li>
                                    <span>No active subscription</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                            {company.md_email && (
                              <div className="col-xl-6 col-lg-6 col-md-6 mb-3">
                                <h5 className="mb-15">MD Information</h5>
                                <ul>
                                  <li>
                                    <span className="fw-6">Email:</span>{" "}
                                    <a href={`mailto:${company.md_email}`}>
                                      {company.md_email}
                                    </a>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>

                        {company.subscribed_modules &&
                          company.subscribed_modules.length > 0 && (
                            <div className="company-info-list mb-25">
                              <h5 className="mb-15">Subscribed Modules</h5>
                              <div className="row">
                                {company.subscribed_modules.map(
                                  (module, index) => (
                                    <div
                                      key={
                                        module.id || module.module_id || index
                                      }
                                      className="col-xl-6 col-lg-6 col-md-6 mb-3"
                                    >
                                      <div className="card">
                                        <div className="card-body">
                                          <h6 className="mb-2">
                                            {module.name ||
                                              module.module_name ||
                                              "Unnamed Module"}
                                          </h6>
                                          {module.description && (
                                            <p
                                              className="text-muted mb-2"
                                              style={{ fontSize: "0.875rem" }}
                                            >
                                              {module.description ||
                                                module.desc}
                                            </p>
                                          )}
                                          {module.status && (
                                            <span
                                              className={`badge ${module.status === "active" ||
                                                  module.status === "Active"
                                                  ? "bg-label-success"
                                                  : "bg-label-danger"
                                                }`}
                                            >
                                              {module.status}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        <div className="company-btn mb-25">
                          <div className="d-flex-between gap-10">
                            <button
                              type="button"
                              className="btn btn-primary w-100"
                              onClick={handleOpenSubscriptionModal}
                            >
                              Add Subscription
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary w-100"
                              data-bs-toggle="modal"
                              data-bs-target="#sendMail"
                            >
                              Terminate Subscription
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* app-content-area-end */}
      </>

      {/* Add Subscription Modal */}
      {showSubscriptionModal && (
        <div
          className={`modal fade ${showSubscriptionModal ? "show" : ""}`}
          id="addSubscriptionModal"
          tabIndex="-1"
          style={{
            display: showSubscriptionModal ? "block" : "none",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          onClick={handleCloseSubscriptionModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <form onSubmit={handleSubscriptionSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Select the Package</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseSubscriptionModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="package_id" className="form-label">
                      Package <span className="text-danger">*</span>
                    </label>
                    {loadingPackages ? (
                      <div className="text-muted">Loading packages...</div>
                    ) : (
                      <select
                        className="form-select"
                        id="package_id"
                        name="package_id"
                        value={subscriptionFormData.package_id}
                        onChange={handleSubscriptionInputChange}
                        required
                      >
                        <option value="">Select a package</option>
                        {packages.map((pkg) => (
                          <option
                            key={pkg.id || pkg.package_id}
                            value={pkg.id || pkg.package_id}
                          >
                            {pkg.name || pkg.package_name || "Unnamed Package"}
                            {pkg.price ? ` - $${pkg.price}` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="end_date" className="form-label">
                      Expiry Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="end_date"
                      name="end_date"
                      value={subscriptionFormData.end_date}
                      onChange={handleSubscriptionInputChange}
                      required
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseSubscriptionModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting || loadingPackages}
                  >
                    {submitting ? "Adding..." : "Add Subscription"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetails;
